import type { AssignmentInfo } from 'api/report/types';
import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { Button } from 'components/Button';
import Header from 'components/Header/Header';
import Content from 'components/Content/Content';
import Footer from 'components/Footer/Footer';
import PageInfoText from 'components/interface/InfoText/desktop/InfoText';
import CheckIcon from '@mui/icons-material/CheckCircleOutlined';
import { LoadingSpinner } from 'components/interface/LoadingSpinner';
import Typography from '@mui/material/Typography';
import AnimateHeight from 'react-animate-height';

const useStyles = makeStyles()(theme => ({
    returnButton: {
        display: 'flex',
        width: '100%',
        marginTop: theme.spacing(2),
        justifyContent: 'space-between',
    },
    submitStatus: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    submitStatusIcon: {
        width: '75px !important',
        height: '75px !important',
        marginBottom: theme.spacing(2),
    },
    submitStatusText: {
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
        userSelect: 'none',
    },
    reportContainer: {
        width: '100%',
        maxWidth: '1100px',
        minWidth: 0,
    },
    timesheetWrapper: {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
}));

interface Props {
    children: JSX.Element;
    assignmentID: string;
    assignment: AssignmentInfo | null;
    year: number | null;
    week: number | null;
    loading: boolean;
    submitting: boolean;
    success: boolean;
    canSubmit: boolean;
    formID: string;
}

export default function View({
    children,
    assignment,
    year,
    week,
    submitting,
    success,
    canSubmit,
    formID,
}: Props): JSX.Element {
    const { classes, cx } = useStyles();
    const history = useHistory();

    const renderSubmittedPage = () => {
        const submittingPage = () => (
            <div className={classes.submitStatus}>
                <LoadingSpinner
                    loading
                    size={75}
                    className={classes.submitStatusIcon}
                />
                <Typography
                    variant='h6'
                    color='textPrimary'
                    className={classes.submitStatusText}
                    align='center'>
                    <b>Skickar in tidrapport</b>
                </Typography>
            </div>
        );
        const successPage = () => (
            <div className={classes.submitStatus}>
                <CheckIcon className={classes.submitStatusIcon} />
                <Typography
                    variant='h6'
                    color='textPrimary'
                    className={classes.submitStatusText}
                    align='center'>
                    <b>Tidrapporten har skickats in</b>
                </Typography>
            </div>
        );
        return success ? successPage() : submittingPage();
    };

    return (
        <>
            <Header />
            <Content>
                <PageInfoText
                    title={assignment?.carePlace?.name}
                    subtitle={assignment ? `${year}, v.${week}` : null}
                />
                <div className={cx(classes.reportContainer, classes.returnButton)}>
                    <Button
                        variant='outlined'
                        onClick={() => {
                            // If there is no history, push the root location and go there.
                            if (history.length < 2) {
                                history.push('/');
                            } else {
                                history.goBack();
                            }
                        }}
                        icon='arrow_back_ios_new'>
                        Gå tillbaka
                    </Button>
                    {success || !canSubmit ? null : (
                        <Button
                            variant='primary'
                            type='submit'
                            form={formID}
                            disabled={submitting}
                            icon='send'
                            iconPosition='end'>
                            Skicka in
                        </Button>
                    )}
                </div>
                <AnimateHeight
                    duration={500}
                    height={!(submitting || success) ? 0 : 'auto'}>
                    {renderSubmittedPage()}
                </AnimateHeight>
                <AnimateHeight
                    className={classes.reportContainer}
                    duration={500}
                    height={submitting || success ? 0 : 'auto'}>
                    <div className={classes.timesheetWrapper}>{children}</div>
                </AnimateHeight>
            </Content>
            <Footer />
        </>
    );
}
